import { RecurrenceFormUnit } from '@griply/types';
import {
  EVERY_DAY_RRULE,
  EVERY_OTHER_DAY_RRULE,
  formUnits,
  formUnitToRruleFreqMap,
  parseIntervalFromRrule,
  setIntervalInRrule,
  setYearMonthDaysInRrule,
} from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect } from 'shared/components/ui/form-select';
import { Frequency, RRule } from 'shared/lib/rrule';

import * as Styled from './form.style';
import { IntervalInput } from './interval-input';

export type HeaderProps = {
  unit: RecurrenceFormUnit;
  rrule: string;
  onChangeUnit: (rrule: string) => void;
  onChangeInterval: (updatedRrule: string) => void;
};

export const Header: React.FC<HeaderProps> = ({
  unit,
  rrule,
  onChangeUnit,
  onChangeInterval,
}) => {
  const { t } = useTranslation();

  const interval = useMemo(() => parseIntervalFromRrule(rrule), [rrule]);

  const _onChangeInterval = (value?: number) => {
    onChangeInterval(setIntervalInRrule(rrule, value ?? 1));
  };

  const _onChangeUnit = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const freq = formUnitToRruleFreqMap[e.target.value as RecurrenceFormUnit];
    switch (freq) {
      case Frequency.YEARLY:
        return onChangeUnit(setYearMonthDaysInRrule(rrule, [], 1));
      case Frequency.MONTHLY:
        return onChangeUnit(new RRule({ freq }).toString());
      case Frequency.DAILY:
        return onChangeUnit(EVERY_OTHER_DAY_RRULE);
      default:
        onChangeUnit(EVERY_DAY_RRULE);
    }
  };

  const formUnitOptions = useMemo(
    () =>
      formUnits.map((formUnit) => ({
        value: formUnit,
        label: t(`forms.habit.fields.repeat.header.units.${formUnit}`, {
          count: interval,
        }),
      })),
    [interval, t],
  );

  return (
    <Styled.HeaderContainer>
      <Typography variant="body2" component="span">
        {t('forms.habit.fields.repeat.header.repeat-every')}
      </Typography>

      <IntervalInput value={interval} onChange={_onChangeInterval} />

      <FormSelect
        value={unit}
        onChange={_onChangeUnit}
        options={formUnitOptions}
        isSmall
      />
    </Styled.HeaderContainer>
  );
};
